import * as React from "react";
import { CreditCard, Truck, RefreshCcw, Mail, MessageCircleQuestionMark } from "lucide-react";
import { Link } from "@/components/common/Link";
import { Button } from "@/components/ui/button";
import { Accordion, AccordionItem, AccordionTrigger, AccordionContent } from "@/components/ui/accordion";
const categories = [{
  id: "eligibility",
  title: "Eligibility & Rules",
  icon: CreditCard
}, {
  id: "contributions",
  title: "Contribution Limits",
  icon: Truck
}, {
  id: "withdrawals",
  title: "Withdrawals & Room",
  icon: RefreshCcw
}];
const faqs = [{
  id: "p1",
  categoryId: "eligibility",
  question: "Who is eligible to open a TFSA in Ontario?",
  answer: "Any individual who is 18 years of age or older and has a valid Social Insurance Number (SIN) is eligible. You must be a resident of Canada for tax purposes to accumulate contribution room."
}, {
  id: "p2",
  categoryId: "eligibility",
  question: "Do I need earned income to contribute?",
  answer: "No, unlike an RRSP, you do not need earned income to generate TFSA contribution room. Room is granted automatically to every eligible Canadian resident each year."
}, {
  id: "p3",
  categoryId: "eligibility",
  question: "Can I have more than one TFSA account?",
  answer: "Yes, you can have multiple TFSA accounts at different financial institutions. However, your total contributions across all accounts must not exceed your available contribution room."
}, {
  id: "s1",
  categoryId: "contributions",
  question: "What is the 2024 TFSA contribution limit?",
  answer: "The annual TFSA contribution limit for 2024 is $7,000. If you have been eligible since the program started in 2009 and have never contributed, your total lifetime room is significantly higher."
}, {
  id: "s2",
  categoryId: "contributions",
  question: "How is my total contribution room calculated?",
  answer: "Your total room is the sum of: your current year's annual limit, any unused room from previous years, and any withdrawals made in the previous calendar year."
}, {
  id: "r1",
  categoryId: "withdrawals",
  question: "Are TFSA withdrawals taxable?",
  answer: "No. One of the primary benefits of a TFSA is that all withdrawals—including the original principal and any investment growth or interest earned—are completely tax-free."
}, {
  id: "r2",
  categoryId: "withdrawals",
  question: "When do I get my withdrawal room back?",
  answer: "When you withdraw funds, that exact amount is added back to your contribution room, but only at the beginning of the following calendar year. Re-contributing in the same year may result in penalties."
}];
export default function FaqSection() {
  return <section data-section-id="224891" id="tfsa-quick-facts" className="w-full py-16 md:py-24 bg-background text-foreground">
      <div className="container mx-auto px-4 md:px-6 max-w-4xl">
        
        {}
        <div className="text-center mb-12 md:mb-16">
          <div className="inline-flex items-center justify-center p-3 bg-primary/10 rounded-md mb-4">
            <MessageCircleQuestionMark className="size-6 text-primary" />
          </div>
          <h2 className="font-default text-3xl md:text-5xl font-bold tracking-tighter mb-4 text-foreground">
            TFSA Quick Facts
          </h2>
          <p className="text-muted-foreground text-lg max-w-2xl mx-auto font-default">
            Essential information about Tax-Free Savings Accounts to help you maximize your Ontario financial planning.
          </p>
        </div>

        {}
        <div className="space-y-12">
          {categories.map((category, i) => {
          const categoryFaqs = faqs.filter(faq => faq.categoryId === category.id);
          const Icon = category.icon;
          return <div key={category.id} data-index={i} className="bg-card rounded-md p-6 md:p-8 shadow-md border border-border">
                <div className="flex items-center gap-3 mb-6 pb-4 border-b border-border">
                  <div className="p-2 bg-primary/10 rounded-md">
                    <Icon className="size-5 text-primary" />
                  </div>
                  <h3 className="font-default text-xl font-bold text-card-foreground tracking-tight">
                    {category.title}
                  </h3>
                </div>

                <Accordion type="single" collapsible className="w-full">
                  {categoryFaqs.map((faq, j) => <AccordionItem key={faq.id} value={faq.id} data-index={j} className="border-border last:border-0">
                      <AccordionTrigger className="text-left font-medium text-base hover:text-primary hover:bg-blue-50/30 transition-colors py-4 px-2 rounded-md">
                        {faq.question}
                      </AccordionTrigger>
                      <AccordionContent className="text-muted-foreground leading-relaxed pb-5 px-2 font-mono text-sm">
                        {faq.answer}
                      </AccordionContent>
                    </AccordionItem>)}
                </Accordion>
              </div>;
        })}
        </div>

        {}
        <div className="mt-16 text-center bg-destructive/5 rounded-md p-8 border border-destructive/20">
          <h4 className="font-default text-xl font-bold mb-2 text-destructive">
            Avoid Over-Contribution Penalties
          </h4>
          <p className="text-muted-foreground mb-6 max-w-md mx-auto font-default">
            The CRA charges a 1% monthly tax on excess TFSA contributions. Use our planner to stay within your legal limits.
          </p>
          <Button asChild size="lg" className="rounded-md px-8 bg-primary hover:bg-primary/90 text-primary-foreground font-default font-semibold shadow-lg">
            <Link to="/TfsaPlannerPage">
              <Mail className="mr-2 size-4" />
              Calculate My Room
            </Link>
          </Button>
        </div>

      </div>
    </section>;
}